import React, { useState } from "react";
import {
  FaCalendarCheck,
  FaBuilding,
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import axios from "axios";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { ButtonLoader4 } from "../../components/ButtonLoader";
import Swal from "sweetalert2";
import { GoEye, GoEyeClosed } from "react-icons/go";

const validatePassword = (password) => {
  return {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    // specialChar: /[@#%^&*]/.test(password),
    specialChar: /[!@#$%^&*()_+={}\[\]:;"'<>,.?/\\|`~]/.test(password),
    noSpaces: !/\s/.test(password),
  };
};
export const ContactFormSchema = Yup.object({
  first_name: Yup.string()
    .required("Enter your first name")
    .max(50, "First name must be at most 50 characters")
    .test("not-empty", "First name cannot be only spaces", value => value && value.trim().length > 0)
    .matches(
      /^[A-Za-z\s'-]+$/,
      "First name can only contain letters, spaces, hyphens, and apostrophes"
    )
    .test("min-trimmed-length", "First name must be at least 2 characters", value => value && value.trim().length >= 2),
  last_name: Yup.string()
    .required("Enter your surname")
    .test("not-empty", "Last name cannot be only spaces", value => value && value.trim().length > 0),
  email: Yup.string().email("Email must be a valid email").matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email must include a valid domain").required("Enter your email").max(250, "Email must be at most 250 characters"),
  password: Yup.string().required("Enter your password").max(128, "Password must be at most 128 characters"),
});
const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  cleaner: false,
};

const SignupPage = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
    noSpaces: false,
  });

  const { values, errors, handleChange, handleSubmit, setFieldValue, touched } =
    useFormik({
      initialValues,
      validationSchema: ContactFormSchema,
      validate: (values) => {
        const validations = validatePassword(values.password);
        setPasswordValidations(validations);

        const allValid = Object.values(validations).every(Boolean);
        if (!allValid) {
          return { password: "Password does not meet all requirements" };
        }
      },
      onSubmit: async (values, action) => {
        setLoading(true);
        await axios
          .post(`${BASE_URL}/api/signup/`, { ...values, cleaner: false })
          .then((response) => {
            // toast.success(response.data.message);
            Swal.fire({
              icon: "success",
              text: response.data.message,
              customClass: {
                confirmButton: "custom-green-button",
              },
            });
            setLoading(false);
            action.resetForm();
          })
          .catch((error) => {
            setLoading(false);
            Swal.fire({
              icon: "error",
              text:
                error?.response?.data.non_field_errors ||
                error?.response?.data.email ||
                "Please give us a moment to fix it. Feel free to try again later",
              customClass: {
                confirmButton: "custom-red-button",
              },
            });
          });
      },
    });

  return (
    <div className="flex lg:flex-row flex-col items-center justify-center h-fit mt-10">
      {/* Left Section */}
      <div className="lg:w-1/2 bg-[#FFF5CE] md:p-12 p-4 flex flex-col justify-center">
        <h2 className="text-3xl font-bold mb-6">Sign Up</h2>
        <form className="xl:space-y-4 lg:space-y-8 space-y-4">
          <div>
            <input
              type="text"
              placeholder="First Name"
              name="first_name"
              value={values.first_name}
              onChange={handleChange}
              className="w-full p-2 border outline-none h-[50px] px-4 rounded-lg"
            />
            {errors.first_name && touched.first_name && (
              <p className="text-red-500 text-[12px] h-[6px] pt-1">
                {errors.first_name || ""}
              </p>
            )}
          </div>
          <div className="pt-2">
            <input
              type="text"
              placeholder="Surname"
              name="last_name"
              value={values.last_name}
              onChange={handleChange}
              className="w-full p-2 border outline-none h-[50px] px-4 rounded-lg"
            />
            {errors.last_name && touched.last_name && (
              <p className="text-red-500 text-[12px] h-[6px]">
                {errors.last_name || ""}
              </p>
            )}
          </div>
          <div className="pt-2">
            <input
              type="email"
              placeholder="Email Address"
              name="email"
              value={values.email}
              onChange={handleChange}
              className="w-full p-2 border outline-none h-[50px] px-4 rounded-lg"
            />
            {errors.email && touched.email && (
              <p className="text-red-500 text-[12px] h-[6px] pt-1">
                {errors.email || ""}
              </p>
            )}
          </div>
          <div className="pt-2">
            <div className="relative w-full">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                name="password"
                value={values.password}
                onChange={handleChange}
                className="w-full p-2 border outline-none h-[50px] px-4 rounded-lg pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showPassword ? <GoEye size={20} /> : <GoEyeClosed size={20} />}
              </button>
            </div>
            {errors.password && touched.password && (
              <p className="text-red-500 text-[12px] h-[6px] pt-1 pb-5">
                {errors.password || ""}
              </p>
            )}
          </div>
          <div className="text-sm text-[#000000] font-normal">
            <p>Password must include:</p>
            <ul className="list-disc ">
              {[
                { label: "At least 8 characters", key: "length" },
                {
                  label: "At least one uppercase letter (A-Z)",
                  key: "uppercase",
                },
                {
                  label: "At least one lowercase letter (a-z)",
                  key: "lowercase",
                },
                { label: "At least one number (0-9)", key: "number" },
                {
                  label: "At least one special character (@#%^&*)",
                  key: "specialChar",
                },
                { label: "No spaces", key: "noSpaces" },
              ].map((item) => (
                <li
                  key={item.key}
                  className={`flex items-center ${passwordValidations[item.key]
                      ? "text-black"
                      : "text-red-500"
                    }`}
                >
                  {passwordValidations[item.key] ? (
                    <FaCheck className="text-green-500 mr-2" />
                  ) : (
                    <FaTimes className="text-red-500 mr-2" />
                  )}
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
          <button
            onClick={handleSubmit}
            className="w-full h-[50px] bg-[#586989] text-white p-2 rounded font-medium"
          >
            {loading ? <ButtonLoader4 /> : <>Sign Up</>}
          </button>
        </form>
        <p className="text-sm mt-4 text-center font-medium">
          Already have an account?{" "}
          <Link to="/login" className="text-[#586989]">
            Sign in
          </Link>
        </p>
        <p className="text-sm mt-4 text-center font-medium">
          Download app from App store,{" "}
          <Link to="/app-link" className="text-[#586989]">
            click here
          </Link>
        </p>
      </div>

      {/* Right Section */}
      <div className="lg:w-1/2 md:p-12 p-4 flex flex-col justify-center">
        <h1 className="text-3xl font-bold text-[#586989] mb-4">
          Streamline Your Holiday Let Housekeeping
        </h1>
        <p className="text-gray-600 mb-6">
          Efficiently manage your property cleaning tasks, track invoices, and
          coordinate with your cleaning staff - all in one place.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#FFF5CE] p-4 rounded shadow flex flex-col gap-1">
            <FaCalendarCheck className="text-[#586989] text-xl" />
            <h3 className="font-semibold">Smart Scheduling</h3>
            <p className="text-gray-600 text-sm">
              Easily schedule cleaning tasks between guest stays with our
              intuitive calendar interface.
            </p>
          </div>
          <div className="bg-[#FFF5CE] p-4 rounded shadow flex flex-col gap-1">
            <FaBuilding className="text-[#586989] text-xl" />

            <h3 className="font-semibold">Property Management</h3>
            <p className="text-gray-600 text-sm">
              Manage multiple properties, set specific cleaning requirements,
              and track completion status.
            </p>
          </div>
          <div className="bg-[#FFF5CE] p-4 rounded shadow flex flex-col gap-1">
            <FaFileInvoiceDollar className="text-[#586989] text-xl" />

            <h3 className="font-semibold">Automated Invoicing</h3>
            <p className="text-gray-600 text-sm">
              Generate and track invoices automatically based on completed
              cleaning tasks.
            </p>
          </div>
          <div className="bg-[#FFF5CE] p-4 rounded shadow flex flex-col gap-1">
            <FaCheckCircle className="text-[#586989] text-xl" />
            <h3 className="font-semibold">Quality Assurance</h3>
            <p className="text-gray-600 text-sm">
              Maintain high standards with task checklists and cleaner
              performance tracking.
            </p>
          </div>
        </div>

        <blockquote className="bg-gray-100 p-4 rounded mt-6 text-gray-700">
          "This platform has revolutionized how we manage our holiday let
          cleaning operations. The automated invoicing alone has saved us hours
          of administrative work each week."
          <br />
          <span className="font-semibold">
            - Sarah Johnson, Property Manager
          </span>
        </blockquote>
      </div>
    </div>
  );
};

export default SignupPage;
